import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axiosConfig';
import { useModal } from '../contexts/ModalContext';

const emptyDraft = () => ({
    name: '',
    contactName: '',
    contactPhone: '',
    memo: '',
    status: 'ACTIVE',
    facilityIds: [],
});

/**
 * 건물주.
 *
 * 우리가 관리해 주는 건물의 주인이다. 시설이 이 사람에게 매이고, 그 사람의
 * 계정은 자기 건물의 것만 본다 -- 점검이 잘 되고 있는지, 무슨 이슈가 있는지,
 * 누가 세 들어 있는지.
 *
 * 표는 처음부터 있었는데 만들 화면이 없어서, 건물주 계정이라는 것이 사실상
 * 존재할 수 없었다.
 */
const ClientListPage = () => {
    const navigate = useNavigate();
    const { showAlert, showConfirm } = useModal();

    const [clients, setClients] = useState([]);
    const [facilities, setFacilities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState(emptyDraft());
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/clients');
            setClients(data || []);
        } catch (err) {
            console.error('Failed to load clients', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        api
            .get('/facilities')
            .then((res) => setFacilities(res.data || []))
            .catch((err) => console.error('Failed to load facilities', err));
    }, []);

    const save = async () => {
        const target = editing ?? draft;
        if (!target.name?.trim()) {
            showAlert('알림', '건물주 이름을 적어 주세요.', 'warning');
            return;
        }
        setSaving(true);
        try {
            const body = {
                name: target.name.trim(),
                contactName: target.contactName,
                contactPhone: target.contactPhone,
                memo: target.memo,
                status: target.status,
            };
            if (editing) {
                // 고칠 때는 건물을 보내지 않는다. 한 건물의 주인은 시설 화면에서
                // 정한다 -- 같은 값을 두 곳에서 고칠 수 있으면 언젠가 서로
                // 어긋나고, 그때 어느 쪽이 맞는지 알 수 없다.
                await api.patch(`/clients/${editing.id}`, body);
            } else {
                await api.post('/clients', { ...body, facilityIds: target.facilityIds });
            }
            setDraft(emptyDraft());
            setEditing(null);
            setOpen(false);
            await load();
        } catch (err) {
            showAlert('오류', err.response?.data?.message || '저장하지 못했습니다.', 'danger');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (client) => {
        const ok = await showConfirm('건물주 삭제', `${client.name}을 지울까요?`, 'danger');
        if (!ok) return;
        try {
            await api.delete(`/clients/${client.id}`);
            await load();
        } catch (err) {
            // 건물이나 계정이 매여 있으면 서버가 막는다. 왜 막혔는지 그대로 낸다.
            showAlert(
                '지울 수 없습니다',
                err.response?.data?.message || '지우지 못했습니다.',
                'warning',
            );
        }
    };

    const field = (key) => ({
        value: (editing ?? draft)[key] ?? '',
        onChange: (e) =>
            editing
                ? setEditing({ ...editing, [key]: e.target.value })
                : setDraft({ ...draft, [key]: e.target.value }),
    });

    return (
        <div className="pb-5">
            <div className="d-flex justify-content-between align-items-start gap-2 mb-3 flex-wrap">
                <div>
                    <h4 className="fw-bold mb-1">건물주</h4>
                    <p className="text-muted small mb-0">
                        건물을 맡긴 분들입니다. 계정을 드리면 자기 건물의 점검·이슈·임차 현황을 봅니다.
                    </p>
                </div>
                <button
                    className="btn btn-primary fw-bold px-3"
                    onClick={() => {
                        setEditing(null);
                        setDraft(emptyDraft());
                        setOpen(!open);
                    }}
                >
                    <i className="bi bi-plus-lg"></i>
                    <span className="d-none d-md-inline ms-2">건물주 등록</span>
                </button>
            </div>

            {(open || editing) && (
                <div className="bg-white rounded-4 border p-3 mb-3">
                    <div className="fw-bold small mb-2">
                        {editing ? `${editing.name} 수정` : '건물주 등록'}
                    </div>
                    <div className="row g-2">
                        <div className="col-12 col-md-3">
                            <input className="form-control" placeholder="상호 또는 이름" {...field('name')} />
                        </div>
                        <div className="col-6 col-md-2">
                            <input className="form-control" placeholder="담당자" {...field('contactName')} />
                        </div>
                        <div className="col-6 col-md-3">
                            <input className="form-control" placeholder="연락처" {...field('contactPhone')} />
                        </div>
                        <div className="col-12 col-md-2">
                            <select className="form-select" {...field('status')}>
                                <option value="ACTIVE">진행</option>
                                <option value="ENDED">종료</option>
                            </select>
                        </div>
                        <div className="col-12 col-md-2 d-flex gap-2">
                            <button className="btn btn-primary flex-grow-1" onClick={save} disabled={saving}>
                                {editing ? '수정' : '등록'}
                            </button>
                            <button
                                className="btn btn-white border"
                                onClick={() => {
                                    setEditing(null);
                                    setOpen(false);
                                }}
                            >
                                취소
                            </button>
                        </div>
                        <div className="col-12">
                            <input className="form-control" placeholder="메모" {...field('memo')} />
                        </div>

                        {/* 만들 때만 고른다. 건물 없는 건물주는 빈 화면을 보게
                            되므로 그 실수를 여기서 막는다. 나중에 주인이 바뀌는
                            일은 드물고, 그때는 시설 쪽에서 고친다. */}
                        {!editing && (
                            <div className="col-12">
                                <div className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                                    맡은 건물
                                </div>
                                {facilities.length === 0 ? (
                                    <div className="text-muted small">등록된 시설이 없습니다.</div>
                                ) : (
                                    <div className="d-flex flex-wrap gap-2">
                                        {facilities.map((f) => {
                                            const picked = draft.facilityIds.includes(f.id);
                                            return (
                                                <button
                                                    key={f.id}
                                                    className={`btn btn-sm rounded-pill px-3 ${picked ? 'btn-primary' : 'btn-white border'}`}
                                                    onClick={() =>
                                                        setDraft({
                                                            ...draft,
                                                            facilityIds: picked
                                                                ? draft.facilityIds.filter((x) => x !== f.id)
                                                                : [...draft.facilityIds, f.id],
                                                        })
                                                    }
                                                >
                                                    {f.name}
                                                    {/* 이미 다른 건물주 것이면 알려 준다. 고르면
                                                        주인이 바뀐다. */}
                                                    {f.clientId && !picked && (
                                                        <span className="ms-1 text-warning">●</span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                                <div className="text-muted mt-1" style={{ fontSize: '0.72rem' }}>
                                    나중에 바꾸는 것은 시설 수정에서 합니다. ● 은 이미 다른 건물주의 건물입니다.
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status"></div>
                </div>
            ) : clients.length === 0 ? (
                <div className="bg-white rounded-4 border text-center p-5">
                    <i className="bi bi-person-badge display-6 d-block mb-3 text-muted opacity-50"></i>
                    <h6 className="fw-bold mb-1">건물주가 없습니다</h6>
                    <p className="text-muted small mb-0">
                        직영 건물만 관리한다면 이 화면은 비워 두셔도 됩니다.
                    </p>
                </div>
            ) : (
                clients.map((c) => (
                    <div key={c.id} className="bg-white rounded-4 border p-3 mb-2">
                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                            <span className="fw-bold">{c.name}</span>
                            {c.status === 'ENDED' && (
                                <span className="badge bg-light text-secondary border fw-normal">종료</span>
                            )}
                            <div className="ms-auto d-flex gap-2">
                                <button
                                    className="btn btn-sm border-0 p-0 text-secondary"
                                    onClick={() => {
                                        setOpen(false);
                                        setEditing(c);
                                    }}
                                >
                                    수정
                                </button>
                                <button
                                    className="btn btn-sm border-0 p-0 text-danger"
                                    onClick={() => remove(c)}
                                >
                                    삭제
                                </button>
                            </div>
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                            {[c.contactName, c.contactPhone].filter(Boolean).join(' · ') || '연락처 없음'}
                        </div>
                        {/* 건물과 계정이 이 사람에게 매여 있다. 지울 수 없는 이유가
                            여기 보이면 따로 물어볼 일이 없다. */}
                        <div className="d-flex flex-wrap gap-2 mt-2">
                            {(c.facilities || []).map((f) => (
                                <span
                                    key={f.id}
                                    className="badge bg-light text-dark border fw-normal"
                                    style={{ fontSize: '0.72rem', cursor: 'pointer' }}
                                    onClick={() => navigate(`/facilities/${f.id}`)}
                                >
                                    {f.name}
                                </span>
                            ))}
                            {(c.facilities || []).length === 0 && (
                                <span className="text-muted" style={{ fontSize: '0.72rem' }}>
                                    맡은 건물이 없습니다. 시설 수정에서 지정해 주세요.
                                </span>
                            )}
                        </div>
                        <div className="text-muted mt-1" style={{ fontSize: '0.72rem' }}>
                            {(c.users || []).length > 0
                                ? `계정 ${c.users.map((u) => u.name).join(', ')}`
                                : '계정 없음 — 사용자 화면에서 역할을 「건물주」로 두면 드릴 수 있습니다.'}
                        </div>
                    </div>
                ))
            )}
        </div>
    );
};

export default ClientListPage;
